import React, { useState, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

const ZwalletConnection = ({ 
  hasExistingAccounts, 
  initZwallet, 
  setShowUnlockModal, 
  onGenerateSeedPhrase,
  onImportAccount,
  onClose,
  zwalletMode
}) => {
  const { login, showZwalletForImport } = useContext(AuthContext);
  
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  const connectZwallet = useCallback(async () => {
    try {
      setConnecting(true);
      setConnectionError('');
      login({ mode: 'connect' });
    } catch (error) {
      console.error('Zwallet connection error:', error);
      setConnectionError(error.message || 'Failed to connect Zwallet');
    } finally {
      setConnecting(false);
    }
  }, [login]);

  const handleZwalletClick = async () => {
    setConnectionError('');
    await connectZwallet();
  };

  // FIX: Proper import handler that prevents re-mounts
  const handleImportClick = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('📥 Import Existing Account clicked - using proper handler');
    
    // Try to use AuthContext import function first
    if (showZwalletForImport) {
      console.log('🔄 Using AuthContext showZwalletForImport');
      showZwalletForImport();
      return;
    }
    
    // Fallback to original onImportAccount prop
    if (onImportAccount) {
      console.log('🔄 Falling back to onImportAccount prop');
      onImportAccount();
      return;
    }
    
    // Last resort: send message
    console.log('🔄 Sending import request message');
    window.parent.postMessage({
      type: 'ZWALLET_IMPORT_REQUESTED',
      source: 'zwallet-connection'
    }, '*');
  }, [showZwalletForImport, onImportAccount]);

  // If we're in create mode, show simplified creation flow
  if (zwalletMode === 'create') {
    return (
      <div className="zwallet-connection" style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
        padding: '20px',
        position: 'relative'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          background: 'rgba(0, 0, 0, 0.9)',
          borderRadius: '20px',
          border: '2px solid var(--gold)',
          padding: '40px 30px',
          boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)',
          textAlign: 'center'
        }}>
          {/* Header */}
          <div style={{ marginBottom: '30px' }}>
            <h1 style={{
              fontSize: '36px',
              color: 'var(--gold)',
              margin: '0 0 20px 0',
              fontWeight: 'bold'
            }}>
              🏦 Zwallet
            </h1>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '16px',
              margin: '0 0 20px 0',
              lineHeight: '1.4'
            }}>
              Your Complete Web3 Financial Ecosystem
            </p>
            <div style={{
              background: 'rgba(255, 215, 0, 0.1)',
              color: 'var(--gold)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              border: '1px solid var(--gold)',
              display: 'inline-block'
            }}>
              Industry-Standard Security
            </div>
          </div>

          {/* Main Content */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              color: 'var(--gold)',
              fontSize: '24px',
              margin: '0 0 30px 0',
              textAlign: 'center'
            }}>
              Create Your Zwallet
            </h2>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              marginBottom: '30px'
            }}>
              {/* Create New Wallet Button - STANDARD FLOW: Seed Phrase FIRST */}
              <button
                onClick={() => {
                  console.log('🖱️ Create New Zwallet button clicked!');
                  console.log('Starting standard flow: Seed Phrase -> Password');
                  if (onGenerateSeedPhrase) {
                    onGenerateSeedPhrase();
                  }
                }}
                style={{
                  width: '100%',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  border: 'none',
                  padding: '18px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 5px 15px rgba(255, 215, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span style={{ fontSize: '20px' }}>🌱</span>
                Create New Wallet (Standard Flow)
              </button>

              {/* Import Wallet Button - UPDATED with requested structure */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🖱️ Import Existing Account clicked!');
                  if (onImportAccount) {
                    onImportAccount();
                  }
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: 'var(--gold)',
                  border: '2px solid var(--gold)',
                  padding: '18px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 215, 0, 0.1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '20px' }}>📥</span>
                Import Existing Account
              </button>

              {/* Connect Existing (only if has accounts) */}
              {hasExistingAccounts && (
                <button
                  onClick={handleZwalletClick}
                  disabled={connecting}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    color: 'var(--text-light)',
                    border: '1px solid var(--border)',
                    padding: '15px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: connecting ? 'not-allowed' : 'pointer',
                    opacity: connecting ? 0.7 : 1,
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  {connecting ? 'Connecting...' : '🔓 Connect Existing Wallet'}
                </button>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div style={{
            background: 'rgba(255, 215, 0, 0.05)',
            border: '1px solid var(--gold)',
            borderRadius: '10px',
            padding: '15px',
            marginTop: '20px'
          }}>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '12px',
              margin: 0,
              lineHeight: '1.6'
            }}>
              <strong>🔐 Standard Flow:</strong> 1) Generate seed phrase → 2) Save it → 3) Set local password
            </p>
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'transparent',
              color: 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  // Normal connection screen - Simple BankSystem style
  return (
    <div className="zwallet-connection" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
      padding: '20px',
      position: 'relative'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'rgba(0, 0, 0, 0.9)',
        borderRadius: '20px',
        border: '2px solid var(--gold)',
        padding: '40px 30px',
        boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)',
        textAlign: 'center'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '36px',
            color: 'var(--gold)',
            margin: '0 0 20px 0',
            fontWeight: 'bold'
          }}>
            🏦 Zwallet
          </h1>
          <div style={{
            background: 'rgba(255, 215, 0, 0.1)',
            color: 'var(--gold)',
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '12px',
            border: '1px solid var(--gold)',
            display: 'inline-block'
          }}>
            Enterprise-Grade Security
          </div>
        </div>

        {/* Main Content */}
        <div style={{ marginBottom: '40px' }}>
          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            marginBottom: '30px'
          }}>
            {hasExistingAccounts ? (
              // Show unlock option if accounts exist
              <button
                onClick={() => setShowUnlockModal(true)}
                style={{
                  width: '100%',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  border: 'none',
                  padding: '18px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 5px 15px rgba(255, 215, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span style={{ fontSize: '20px' }}>🔓</span>
                Unlock Zwallet
              </button>
            ) : (
              // Show create option if no accounts - STANDARD FLOW
              <button
                onClick={() => {
                  console.log('🖱️ Create Wallet button clicked!');
                  console.log('Starting standard flow: Seed Phrase -> Password');
                  if (onGenerateSeedPhrase) {
                    onGenerateSeedPhrase();
                  }
                }}
                style={{
                  width: '100%',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  border: 'none',
                  padding: '18px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 5px 15px rgba(255, 215, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span style={{ fontSize: '20px' }}>🌱</span>
                Create Wallet (Standard)
              </button>
            )}

            {/* Import Wallet Button - UPDATED with requested structure */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Import Existing Account clicked!');
                if (onImportAccount) {
                  onImportAccount();
                }
              }}
              style={{
                width: '100%',
                background: 'transparent',
                color: 'var(--gold)',
                border: '2px solid var(--gold)',
                padding: '18px',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 215, 0, 0.1)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '20px' }}>📥</span>
              Import Existing Wallet
            </button>
          </div>

          {/* Existing Accounts Section */}
          {hasExistingAccounts && (
            <div style={{
              background: 'rgba(255, 215, 0, 0.05)',
              border: '1px solid var(--gold)',
              borderRadius: '10px',
              padding: '15px',
              marginTop: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '10px'
              }}>
                <span style={{ fontSize: '18px', color: 'var(--gold)' }}>💼</span>
                <h3 style={{
                  color: 'var(--gold)',
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  Existing Zwallet Accounts Detected
                </h3>
              </div>
              <p style={{
                color: 'var(--text-secondary)',
                margin: '0 0 15px 0',
                fontSize: '12px',
                textAlign: 'center'
              }}>
                Click "Unlock Zwallet" above to access your wallet
              </p>
              <button
                onClick={() => setShowUnlockModal(true)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: 'var(--gold)',
                  border: '1px solid var(--gold)',
                  padding: '10px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 215, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                <span>🔓</span>
                Unlock & Manage Accounts
              </button>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div style={{
          background: 'rgba(255, 215, 0, 0.05)',
          border: '1px solid var(--gold)',
          borderRadius: '10px',
          padding: '15px',
          marginTop: '20px'
        }}>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '12px',
            margin: 0,
            lineHeight: '1.6'
          }}>
            <strong>🔐 Security Standard:</strong> Seed phrase = master key. Password = local encryption only.
          </p>
        </div>
      </div>

      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'transparent',
            color: 'var(--gold)',
            border: '1px solid var(--gold)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default ZwalletConnection;
